import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { UserCompany } from '@gumahub/shared';
import { useBranding } from '../contexts/BrandingContext';

interface CompanySwitcherProps {
  companies: UserCompany[];
  currentCompanyId: string;
  onSwitch: (clientId: string) => Promise<boolean>;
}

const Icons = {
  Building: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  ),
  ChevronDown: () => (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  ),
  Check: () => (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  ),
  Loader: () => (
    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  ),
};

const CompanySwitcher: React.FC<CompanySwitcherProps> = ({
  companies,
  currentCompanyId,
  onSwitch,
}) => {
  const { t } = useTranslation();
  const { clearBranding } = useBranding();
  const [isOpen, setIsOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  const currentCompany = companies.find(c => c.clientId === currentCompanyId);

  const handleSwitch = async (clientId: string) => {
    if (clientId === currentCompanyId || isSwitching) return;

    setIsSwitching(true);
    try {
      const success = await onSwitch(clientId);
      if (success) {
        setIsOpen(false);
        // Clear branding before reload to prevent showing stale branding
        clearBranding();
        // Reload page to refresh all data with new company context
        window.location.reload();
      }
    } finally {
      setIsSwitching(false);
    }
  };

  // Don't render if only one company
  if (companies.length <= 1) return null;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isSwitching}
        className="flex items-center gap-2 px-3 py-2 rounded-xl text-slate-600 hover:bg-slate-100 hover:text-slate-800 transition-colors disabled:opacity-50"
      >
        {isSwitching ? <Icons.Loader /> : <Icons.Building />}
        <span className="hidden sm:inline text-sm font-medium max-w-[120px] truncate">
          {currentCompany?.clientName || t('common:companySwitcher.selectCompany')}
        </span>
        <Icons.ChevronDown />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50 animate-fade-in">
            <div className="px-3 py-2 border-b border-slate-100">
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                {t('common:companySwitcher.switchCompany')}
              </span>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {companies.map((company) => (
                <button
                  key={company.clientId}
                  onClick={() => handleSwitch(company.clientId)}
                  disabled={isSwitching}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50 transition-colors disabled:opacity-50 ${
                    company.clientId === currentCompanyId
                      ? 'bg-guma-50 text-guma-600'
                      : 'text-slate-700'
                  }`}
                >
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 flex-shrink-0">
                    <span className="text-sm font-semibold">
                      {company.clientCode?.substring(0, 2).toUpperCase() || company.clientName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{company.clientName}</p>
                    <p className="text-xs text-slate-400 truncate">{company.clientCode}</p>
                  </div>
                  {company.clientId === currentCompanyId && (
                    <span className="text-guma-500 flex-shrink-0">
                      <Icons.Check />
                    </span>
                  )}
                  {company.isDefault && company.clientId !== currentCompanyId && (
                    <span className="text-xs text-slate-400 px-1.5 py-0.5 bg-slate-100 rounded flex-shrink-0">
                      {t('common:companySwitcher.default')}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CompanySwitcher;
